import { useCallback, useEffect, useState } from 'react';
import { getTenantId } from '../services/tenantService';
import { themeService } from '../services/themeService';
import { isDevAdaptationEnabled } from '../utils/devAdaptation';
import devThemeData from '../utils/devTheme.json';

export const useLogos = () => {
  const [theme, setTheme] = useState(null);
  const [tenantId, setTenantId] = useState(getTenantId());

  const loadTheme = useCallback(async () => {
    try {
      const currentTenantId = getTenantId();
      if (!currentTenantId) {
        console.warn('Tenant ID não encontrado, aguardando inicialização...');
        return;
      }

      setTenantId(currentTenantId);

      // DEV ADAPTATION: usa tema local (devTheme.json) sem chamar o endpoint
      if (isDevAdaptationEnabled) {
        setTheme(devThemeData);
        return;
      }

      // Obtém o idioma atual
      const currentLanguage = localStorage.getItem('i18nextLng') || 'pt-BR';
      const normalizedLang = currentLanguage === 'pt' ? 'pt-BR' :
                            currentLanguage === 'en' ? 'en-US' :
                            currentLanguage;

      // Busca tema usando o serviço de assets (que já gerencia profile baseado em autenticação)
      const themeData = await themeService.fetchTheme(currentTenantId, normalizedLang);
      
      if (themeData) {
        localStorage.setItem('theme', JSON.stringify(themeData));
        setTheme(themeData);
      } else {
        // Fallback para cache se disponível
        const cachedTheme = localStorage.getItem('theme');
        if (cachedTheme) {
          setTheme(JSON.parse(cachedTheme));
        }
      }
    } catch (error) {
      console.error('Erro ao carregar tema:', error);
      // Fallback para cache em caso de erro
      const cachedTheme = localStorage.getItem('theme');
      if (cachedTheme) {
        setTheme(JSON.parse(cachedTheme));
      }
    }
  }, []);

  useEffect(() => {
    loadTheme();
    
    // Escuta mudanças de autenticação para recarregar tema
    const handleAuthChange = () => {
      // Limpa cache do themeService e localStorage para forçar recarregamento com profile correto
      const currentTenantId = getTenantId();
      if (currentTenantId) {
        themeService.clearCacheForClient(currentTenantId);
      }
      localStorage.removeItem('theme');
      loadTheme();
    };
    
    // Escuta mudanças no token (login/logout)
    const handleStorageChange = (e) => {
      if (e.key === 'idToken' || e.key === 'tokenExpiresAt') {
        handleAuthChange();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('themeUpdated', loadTheme);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('themeUpdated', loadTheme);
    };
  }, [loadTheme]);

  const getLogoInfos = useCallback((local, type) => {
    if (!theme) return null;
    
    if (local === 'brand' && type === 'manifest') {
      return `/manifest.json`;
    }
    
    return theme?.brand?.[local]?.[type];
  }, [theme, tenantId]);

  const getLogoPath = (local, type) => {
    return getLogoInfos(local, type);
  };

  return {
    getLogo: getLogoPath,
    theme,
    clientId: tenantId
  };
};
